import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./AdminMasterItems.css";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://api.magnirauniverse.com";

const AdminMasterItems = () => {
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/quote-master`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch error:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncSheet = async () => {
    if (!window.confirm("Want to update latest data from sheet?")) return;

    try {
      setLoading(true);
      const res = await axios.post(`${BASE_URL}/api/quote-master/sync-sheets`);
      alert(`${res.data.message}. Total inserted: ${res.data.inserted}`);
      fetchItems();
    } catch (err) {
      console.error("Sync error full:", err);
      alert(err.response?.data?.message || err.message || "Sheet sync failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const resolveImage = (img) => {
    if (!img) return "";
    if (img.startsWith("http")) return img;
    return `/${img}`;
  };

  const handleSearch = () => {
    setAppliedSearch(searchText.trim());
  };

  const handleClearSearch = () => {
    setSearchText("");
    setAppliedSearch("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;

    try {
      await axios.delete(`${BASE_URL}/api/quote-master/${id}`);
      fetchItems();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete item");
    }
  };

  const handleSave = async (item) => {
    try {
      await axios.put(`${BASE_URL}/api/quote-master/${item._id}`, item);
      alert("Item updated successfully");
      fetchItems();
    } catch (err) {
      console.error("Save error:", err);
      alert("Failed to update item");
    }
  };

  const handleChange = (id, field, value) => {
    setItems((prev) =>
      prev.map((item) =>
        item._id === id ? { ...item, [field]: value } : item,
      ),
    );
  };

  const filteredItems = useMemo(() => {
    const search = appliedSearch.toLowerCase();

    if (!search) return items;

    return items
      .filter((item) => {
        const text = `
          ${item.label || ""}
          ${item.description || ""}
          ${item.category || ""}
          ${item.length || ""}
          ${item.width || ""}
          ${item.MRP || ""}
          ${item.vendorCost || ""}
        `.toLowerCase();

        return text.includes(search);
      })
      .sort((a, b) => {
        const aLabel = String(a.label || "").toLowerCase();
        const bLabel = String(b.label || "").toLowerCase();

        const aStart = aLabel.startsWith(search) ? 0 : 1;
        const bStart = bLabel.startsWith(search) ? 0 : 1;

        return aStart - bStart || aLabel.localeCompare(bLabel);
      });
  }, [items, appliedSearch]);

  return (
    <div className="admin-master-wrapper">
      <div className="admin-master-header">
        <div>
          <h2>Admin Quote Master</h2>
          <p>Manage quote items, pricing, category and sheet sync</p>
        </div>

        <div className="search-bar">
          <input
            type="text"
            placeholder="Search item, category, MRP..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />

          <button className="search-btn" onClick={handleSearch}>
            Search
          </button>

          <button className="clear-btn" onClick={handleClearSearch}>
            Clear
          </button>

          <button className="sync-btn" onClick={handleSyncSheet}>
            Sync Sheet
          </button>
        </div>
      </div>

      <div className="table-card">
        <div className="table-topbar">
          <span>Total Items: {filteredItems.length}</span>
          {appliedSearch && <span>Search: "{appliedSearch}"</span>}
        </div>

        <div className="table-responsive">
          <table className="quote-master-table">
            <thead>
              <tr>
                <th className="col-image">Image</th>
                <th className="col-label">Label</th>
                <th className="col-description">Description</th>
                <th className="col-category">Category</th>
                <th className="col-small">Length</th>
                <th className="col-small">Width</th>
                <th className="col-medium">MRP</th>
                <th className="col-medium">Vendor Cost</th>
                <th className="col-action">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="no-data">
                    Loading...
                  </td>
                </tr>
              ) : filteredItems.length > 0 ? (
                filteredItems.map((item) => (
                  <tr key={item._id}>
                    <td>
                      {item.image ? (
                        <img
                          src={resolveImage(item.image)}
                          alt=""
                          className="item-image"
                        />
                      ) : (
                        <div className="no-image">No Image</div>
                      )}
                    </td>

                    <td>
                      <textarea
                        className="table-textarea label-box"
                        value={item.label || ""}
                        onChange={(e) =>
                          handleChange(item._id, "label", e.target.value)
                        }
                      />
                    </td>

                    <td>
                      <textarea
                        className="table-textarea desc-box"
                        value={item.description || ""}
                        onChange={(e) =>
                          handleChange(item._id, "description", e.target.value)
                        }
                      />
                    </td>

                    <td>
                      <select
                        className="table-select"
                        value={item.category || ""}
                        onChange={(e) =>
                          handleChange(item._id, "category", e.target.value)
                        }
                      >
                        <option value="">Select</option>
                        <option value="Kitchen">Kitchen</option>
                        <option value="Loose Furniture">Loose Furniture</option>
                        <option value="Site Service">Site Service</option>
                        <option value="Godrej Interio">Godrej Interio</option>
                      </select>
                    </td>

                    <td>
                      <input
                        className="table-input"
                        type="number"
                        value={item.length || ""}
                        onChange={(e) =>
                          handleChange(item._id, "length", e.target.value)
                        }
                      />
                    </td>

                    <td>
                      <input
                        className="table-input"
                        type="number"
                        value={item.width || ""}
                        onChange={(e) =>
                          handleChange(item._id, "width", e.target.value)
                        }
                      />
                    </td>

                    <td>
                      <input
                        className="table-input"
                        type="number"
                        value={item.MRP || ""}
                        onChange={(e) =>
                          handleChange(item._id, "MRP", e.target.value)
                        }
                      />
                    </td>

                    <td>
                      <input
                        className="table-input"
                        type="number"
                        value={item.vendorCost || ""}
                        onChange={(e) =>
                          handleChange(item._id, "vendorCost", e.target.value)
                        }
                      />
                    </td>

                    <td>
                      <div className="action-buttons">
                        <button
                          className="save-btn"
                          onClick={() => handleSave(item)}
                        >
                          Save
                        </button>

                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(item._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="no-data">
                    No matching items found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminMasterItems;
